/* Screen 1-1 - Select New Plan */

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import goldIcon from "../../../../assets/images/Gold.svg";
import listblackicon from "../../../../assets/images/list-black.svg";
import platiniumIcon from "../../../../assets/images/Platinium.svg";
import silverIcon from "../../../../assets/images/Silver.svg";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import ViewSummaryPopup from "../../../../components/newMotorPolicy/veiwSummaryPopup/viewSummaryPopup";
import TravelBenefitsModal from "../../../../components/newBniTravelPolicy/modals/travelBenefitsModal";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useMotorForm from "../../../../hooks/useMotorFormData";
import useTravelRenewalFlow, {
  TRAVEL_RENEWAL_SELECTED_PLAN,
} from "../../../../hooks/useTravelRenewalFlow";
import { PolicyViewResponse } from "../../../../redux/features/motor/getQuoteSlice";
import {
  fetchBniTravelPlanPrices,
  selectTravelPlanPricesLoading,
} from "../../../../redux/features/quoteSlice";
import PlanCard from "../../../motorInsurancePages/newMotorPolicy/planPages/planCard";
import TravelRenewalLayout from "../components/TravelRenewalLayout";

// Plan code to display info mapping
// Old codes (SILVER/GO/PL) kept for existing policies, new codes for new travel plans
const planDisplayMap = {
  SILVER: {
    image: silverIcon,
    name: "Silver",
    name_ar: "الفضية",
  },
  GO: {
    image: goldIcon,
    name: "Gold",
    name_ar: "الذهبية",
  },
  PL: {
    image: platiniumIcon,
    name: "Platinum",
    name_ar: "البلاتينية",
  },
  SCHENGENB: {
    image: silverIcon,
    name: "Schengen",
    name_ar: "شنغن",
  },
  WLEXB: {
    image: goldIcon,
    name: "Worldwide (Excl. USA & Canada)",
    name_ar: "جميع دول العالم (باستثناء أمريكا وكندا)",
  },
  WLB: {
    image: platiniumIcon,
    name: "Worldwide",
    name_ar: "جميع دول العالم",
  },
};

// Travel plans data aligned with the new travel policy planChoice component (BNI plans)
const TRAVEL_PLANS = [
  {
    id: "schengen",
    code: "SCHENGENB",
    name: "Schengen",
    name_ar: "شنغن",
    imgSrc: silverIcon,
    isRecommended: false,
    weightage: 1,
    features: [
      {
        en: "Essential travel protection for Schengen-area destinations. Covers emergency medical expenses, evacuation, repatriation, and trip cancellation, everything you need to meet visa requirements and travel with confidence.",
        ar: "حماية سفر أساسية لوجهات منطقة شنغن. تشمل مصاريف العلاج الطبي الطارئ، والإخلاء، والترحيل، وإلغاء الرحلة، كل ما تحتاجه لاستيفاء متطلبات التأشيرة والسفر بثقة.",
      },
    ],
  },
  {
    id: "worldwide_ex",
    code: "WLEXB",
    name: "Worldwide",
    name_ar: "جميع دول العالم",
    subtitle: "EXCLUDING USA & CANADA",
    subtitle_ar: "باستثناء الولايات المتحدة الأمريكية وكندا",
    imgSrc: goldIcon,
    isRecommended: true,
    weightage: 2,
    features: [
      {
        en: "Comprehensive global coverage excluding USA & Canada. Includes medical, personal accident, baggage, and legal assistance benefits for worry-free travel worldwide.",
        ar: "تغطية شاملة لجميع دول العالم باستثناء أمريكا وكندا. تشمل المزايا الطبية والحوادث الشخصية والأمتعة والمساعدة القانونية لسفر بلا قلق.",
      },
    ],
  },
  {
    id: "worldwide",
    code: "WLB",
    name: "Worldwide",
    name_ar: "جميع دول العالم",
    imgSrc: platiniumIcon,
    isRecommended: false,
    weightage: 3,
    features: [
      {
        en: "Our highest-tier plan with maximum global protection, including USA & Canada. Full medical, accident, liability, and hijacking coverage for total peace of mind.",
        ar: "خطتنا الأشمل بأعلى مستوى من الحماية العالمية، تشمل أمريكا وكندا. تغطية طبية وحوادث ومسؤولية واختطاف لراحة بال كاملة.",
      },
    ],
  },
];

// Initialize plans without static pricing
const getBaseTravelPlans = () =>
  TRAVEL_PLANS.map((plan) => ({
    ...plan,
    price: null,
    price_ar: null,
    premium: null,
    tax: null,
    totalPremium: null,
    hasValidQuote: false,
    apiDataLoaded: false,
  }));

const SelectNewPlanPage = ({ handleNavigationToRenewalHub }) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();

  // Policy view data
  const policyViewResponseDetails = useSelector(PolicyViewResponse);
  const planPricesLoading = useSelector(selectTravelPlanPricesLoading);

  // Flow hook
  const { goToStep, setHasPlanChanged } = useTravelRenewalFlow();

  // State
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentPlan, setCurrentPlan] = useState("");
  const [plans, setPlans] = useState([]);
  const [ratesLoaded, setRatesLoaded] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [hasTriggeredInitialLoad, setHasTriggeredInitialLoad] = useState(false);

  // Track whether the current plan code has been established once, so the
  // current-plan effect doesn't wipe a restored selection on initial mount.
  const currentPlanInitialized = useRef(false);

  // Motor form hook to save AND restore the selected plan (restores on back navigation)
  const { saveMotorDataInRedux } = useMotorForm(
    TRAVEL_RENEWAL_SELECTED_PLAN,
    setSelectedPlan
  );

  // Get current plan info for the card
  const currentPlanCode = policyViewResponseDetails?.PolicyHeader?.PlanCode;
  const currentPlanInfo = planDisplayMap[currentPlanCode] || planDisplayMap.SILVER;

  // Premium API not available yet — only show price when a new plan is selected via plan-prices API
  const displayPrice = selectedPlan?.totalPremium || 0;

  // Set current plan from policy view
  useEffect(() => {
    if (!policyViewResponseDetails?.PolicyHeader) return;

    const planCode = policyViewResponseDetails?.PolicyHeader?.PlanCode;
    if (currentPlan !== planCode) {
      setCurrentPlan(planCode);
      // Only clear the user's selection when the current plan genuinely changes
      // after being established — not on the initial mount, otherwise we'd wipe
      // the selection restored from Redux on back navigation.
      if (currentPlanInitialized.current) {
        setSelectedPlan(null);
      }
      currentPlanInitialized.current = true;
    }
  }, [policyViewResponseDetails, currentPlan]);

  // Load base plans and fetch prices from API
  useEffect(() => {
    const basePlans = getBaseTravelPlans();
    setPlans(basePlans);

    if (policyViewResponseDetails?.PolicyHeader && !hasTriggeredInitialLoad) {
      setHasTriggeredInitialLoad(true);
      loadTravelPlanPrices();
    }
  }, [policyViewResponseDetails]);

  // Load travel plan prices from API
  const loadTravelPlanPrices = async () => {
    if (planPricesLoading) return;

    try {
      const currentPlans = plans.length > 0 ? plans : getBaseTravelPlans();
      const policyHeader = policyViewResponseDetails?.PolicyHeader;
      const planType = policyHeader?.PlanType || "INDIVIDUAL";
      const fromDateStr = policyHeader?.FromDateStr;
      const toDateStr = policyHeader?.ToDateStr;

      // Convert dates to required format
      const formatDateForAPI = (dateStr) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      };

      const apiFormData = {
        departureDate: formatDateForAPI(fromDateStr),
        returnDate: formatDateForAPI(toDateStr),
        planType: planType.toLowerCase(),
        isAnnual: false,
        passengers:
          planType === "FAMILY"
            ? Array.from({ length: 2 }, (_, i) => ({ id: i + 1 }))
            : undefined,
      };

      const result = await dispatch(
        fetchBniTravelPlanPrices({
          formData: apiFormData,
          options: { timeout: 30000 },
        })
      ).unwrap();

      const apiResult = result.result;

      if (apiResult && apiResult.success && apiResult.data) {
        const plansFromAPI = apiResult.data.Plans || [];

        const planCodeToId = {
          SCHENGENB: "schengen",
          WLEXB: "worldwide_ex",
          WLB: "worldwide",
        };

        let updatedPlans = [];
        let successfulPlans = 0;

        updatedPlans = currentPlans.map((plan) => {
          const planData = plansFromAPI.find((p) => {
            const apiPlanId = planCodeToId[p.PlanCode];
            return apiPlanId === plan.id;
          });

          if (planData && planData.TotalPremium !== undefined && planData.TotalPremium !== null) {
            const apiTotalPremium = planData.TotalPremium || 0;
            successfulPlans++;

            return {
              ...plan,
              price: `BHD ${apiTotalPremium.toFixed(3)}`,
              price_ar: `${apiTotalPremium.toFixed(3)} د.ب`,
              premium: planData.Premium || apiTotalPremium * 0.909,
              tax: planData.Tax || apiTotalPremium * 0.091,
              totalPremium: apiTotalPremium,
              apiData: {
                ...planData,
                quoteNumber: `TRV-${Date.now()}-${plan.id.toUpperCase()}`,
              },
              hasValidQuote: true,
              apiDataLoaded: true,
            };
          } else {
            return {
              ...plan,
              hasValidQuote: false,
              apiDataLoaded: false,
            };
          }
        });

        if (successfulPlans > 0) {
          setPlans(updatedPlans);
        } else {
          setLoadingError(true);
          const fallbackPlans = currentPlans.map((plan) => ({
            ...plan,
            hasValidQuote: false,
            apiDataLoaded: false,
          }));
          setPlans(fallbackPlans);
        }
      } else {
        setLoadingError(true);
      }
    } catch (error) {
      console.error("Failed to load travel plan prices:", error);
      setLoadingError(true);
    } finally {
      setRatesLoaded(true);
    }
  };

  // Plan selection handler
  const planOnClickHandler = (plan) => {
    // If user selects the same plan as current, don't allow continuation
    if (plan.code === currentPlan) {
      setSelectedPlan(null);
      return;
    }

    setSelectedPlan(plan);
    saveMotorDataInRedux(plan);
  };

  // For renewal flow, show all plans (user can upgrade or downgrade)
  const getVisiblePlans = () => {
    return plans.map((plan) => ({ ...plan, isShown: true }));
  };

  const updatedPlans = getVisiblePlans();

  // Benefits Modal Handler
  const handleViewBenefits = () => {
    setShowBenefitsModal(true);
  };

  // Show Summary Modal Handler - always allow showing summary (base premium or selected plan)
  const handleShowSummary = () => {
    setShowSummaryModal(true);
  };

  // Handle back button click - go back to Review Policy Plan
  const handleBackClick = () => {
    goToStep(0); // Go back to Review Policy Plan
  };

  // Manual retry handler
  const handleManualRetry = () => {
    setLoadingError(false);
    setHasTriggeredInitialLoad(false);
    loadTravelPlanPrices();
  };

  // Continue handler - go to Enhance Coverage (step 2)
  const continueClickHandler = () => {
    if (selectedPlan) {
      setHasPlanChanged(true); // Mark that plan was changed
      goToStep(2); // Go to Enhance Coverage
    }
  };

  return (
    <>
      <TravelRenewalLayout
        is_hide_back_btn={false}
        show_close_btn={false}
        isFirstStep={false}
        backClickHandler={handleBackClick}
        btn_disabled={!selectedPlan || selectedPlan?.code === currentPlan}
        show_summary={true}
        btn_onClick={continueClickHandler}
        price={displayPrice}
        onShowSummary={handleShowSummary}
      >
        <div className="selectNewPlan p-4 pt-0 w-[33%] max-350:w-[100%] max767:w-[100%] ipad:w-[55%] mx-auto max767:p-5 max767:pb-0">
          <TitleDescription
            title={translate("Review Your Policy Plan", "راجع خطة وثيقتك")}
            description={translate(
              "You can either continue with your current plan or choose to downgrade.",
              "يمكنك إما الاستمرار بخطتك الحالية أو اختيار التخفيض."
            )}
          />

          {/* Current Plan Details Card */}
          <div className="bg-[#F6F8F9] rounded-2xl p-6 mb-6 mt-6">
            {/* Current Plan Details Header */}
            <h4
              className="mb-4"
              style={{ fontSize: "16px", fontWeight: 600, color: "#1A2024" }}
            >
              {translate("Current Plan Details", "تفاصيل الخطة الحالية")}
            </h4>

            {/* Plan Icon and Name */}
            <div className="flex items-center gap-4">
              <img
                src={currentPlanInfo.image}
                alt={translate(currentPlanInfo.name, currentPlanInfo.name_ar)}
                className="w-12 h-12 object-contain"
              />
              <h3 className="text-[#1A2024] text-2xl font-semibold">
                {translate(currentPlanInfo.name, currentPlanInfo.name_ar)}
              </h3>
            </div>
          </div>
        </div>

        <div className="selectyourcard pb-20 max767:pb-32">
          <div className="max767:w-full max767:overflow-x-scroll max767:pt-0 ipad:overflow-x-hidden ipad:w-[100%] ipad:py-10 mx-auto ipad:pb-8 max767:mb-8">
            <div className="flex gap-4 mb-4 max767:w-max max767:pl-5 max767:pr-5 max767:py-0 max767:pt-5 ipad:w-[100%] ipad:justify-center ipad:items-center ipad:content-center ipad:my-0 ipad:flex-wrap justify-center">
              {updatedPlans.map(
                (plan) =>
                  plan.isShown && (
                    <PlanCard
                      key={plan.code}
                      planOnClickHandler={planOnClickHandler}
                      selectedPlan={selectedPlan}
                      plan={plan}
                      isCurrentPlan={plan.code === currentPlan}
                      showSelectButton
                      showPriceLoader={planPricesLoading}
                      cardClassName="!min-h-[372px]"
                    />
                  )
              )}
            </div>

            {/* Show retry button if loading failed */}
            {loadingError && ratesLoaded && (
              <button
                className="flex items-center mx-auto gap-2 border bg-red-50 border-red-200 text-red-600 px-4 py-2 rounded-[10px] text-sm not-italic font-semibold leading-6 tracking-[-0.084px] mt-4 hover:bg-red-100"
                onClick={handleManualRetry}
              >
                🔄 {translate("Retry Loading Plans", "إعادة تحميل الخطط")}
              </button>
            )}
          </div>

          {/* Benefits buttons */}
          {ratesLoaded && plans.length > 0 && (
            <>
              <button
                className="flex items-center mx-auto gap-2 border bg-white px-4 py-2 rounded-[10px] border-solid border-[#DDE2E4] text-[#1A2024] text-sm not-italic font-semibold leading-6 tracking-[-0.084px] ipad:mt-4 max767:hidden"
                onClick={handleViewBenefits}
              >
                <img src={listblackicon} alt="View benefits" />
                {translate(
                  "View full list of benefits & covers",
                  "عرض القائمة الكاملة للفوائد والتغطيات"
                )}
              </button>
              <button
                className="flex items-center mx-auto gap-2 border bg-white px-4 py-2 rounded-[10px] border-solid border-[#DDE2E4] text-[#48535B] text-sm not-italic font-semibold leading-6 tracking-[-0.084px] ipad:mt-4 hidden max767:block max767:flex"
                onClick={handleViewBenefits}
              >
                <img src={listblackicon} alt="View benefits" />
                {translate(
                  "View full list of benefits & covers",
                  "عرض القائمة الكاملة للفوائد والتغطيات"
                )}
              </button>
            </>
          )}
        </div>
      </TravelRenewalLayout>

      {/* Benefits Modal */}
      <TravelBenefitsModal
        show={showBenefitsModal}
        onClose={() => setShowBenefitsModal(false)}
        plans={updatedPlans}
      />

      {/* Summary Modal */}
      <ViewSummaryPopup
        show={showSummaryModal}
        setShow={setShowSummaryModal}
        passedFrom="TravelRenewal"
      />
    </>
  );
};

export default SelectNewPlanPage;
